import { useT } from '@/lib/i18n';
import { useSourceStore } from '@/lib/state/sourceStore';
import { useState } from 'react';

interface TextPasteFormProps {
  onDone?: () => void;
}

export function TextPasteForm({ onDone }: TextPasteFormProps) {
  const { t, lang } = useT();
  const addSource = useSourceStore((s) => s.addSource);
  const existingTextCount = useSourceStore(
    (s) => s.sources.filter((src) => src.kind === 'text').length,
  );

  const [label, setLabel] = useState('');
  const [text, setText] = useState('');

  const trimmedText = text.trim();
  const disabled = trimmedText.length === 0;

  const submit = () => {
    if (disabled) return;
    const finalLabel = label.trim() || t('input.text.default_label', { n: existingTextCount + 1 });
    addSource({ kind: 'text', label: finalLabel, text: trimmedText });
    setLabel('');
    setText('');
    onDone?.();
  };

  return (
    <div className="flex flex-col gap-2" data-testid="text-paste-form">
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={t('input.text.label_placeholder')}
        className="px-3 py-2 border-2 border-ink rounded-control font-hand text-[15px] bg-paper focus:outline-none focus:ring-3 focus:ring-accent-blue/40"
        aria-label={t('input.text.label_aria')}
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('input.text.body_placeholder')}
        rows={6}
        className="px-3 py-2 border-2 border-ink rounded-control font-hand text-[15px] bg-paper resize-y focus:outline-none focus:ring-3 focus:ring-accent-blue/40"
        aria-label={t('input.text.body_aria')}
        data-testid="text-paste-textarea"
      />
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <span className="font-caveat text-ink-soft text-[15px]">
          {t('source.chars', {
            n: trimmedText.length.toLocaleString(lang === 'en' ? 'en-US' : 'fr-FR'),
          })}
        </span>
        <button
          type="button"
          onClick={submit}
          disabled={disabled}
          className="px-4 py-2 min-h-[40px] bg-accent-blue text-white font-hand text-[15px] border-2 border-ink rounded-control shadow-ink-sm disabled:opacity-40 disabled:cursor-not-allowed hover:translate-y-[-1px] transition-transform"
          data-testid="text-paste-add"
        >
          {t('input.text.add')}
        </button>
      </div>
    </div>
  );
}
